/**
 * Loghi dei provider social usati per la registrazione, come SVG
 * inline. La versione di lucide-react in uso (1.x) non esporta
 * Facebook/Instagram, e Google ha vincoli di brand: usare SVG
 * inline è la via più stabile.
 *
 * Usati da:
 *   · components/header/AuthMenu — pulsanti del popover "Accedi"
 *   · components/servizi/legal-desk/DueDiligenceDesk — pulsanti
 *     della card "registrazione" embedded in chat
 *   · components/header/AuthMenu — glifo provider nella pillola
 *     utente loggato
 */

import type { AuthProvider } from "@/context/AuthProvider";

export function GoogleG({ size = 18 }: { size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
      <path
        fill="#4285F4"
        d="M21.6 12.227c0-.708-.064-1.39-.182-2.045H12v3.868h5.382a4.6 4.6 0 0 1-1.995 3.018v2.51h3.231c1.891-1.741 2.982-4.305 2.982-7.351z"
      />
      <path
        fill="#34A853"
        d="M12 22c2.7 0 4.964-.895 6.618-2.423l-3.231-2.509c-.895.6-2.04.955-3.387.955-2.605 0-4.811-1.76-5.596-4.123H3.064v2.59A9.996 9.996 0 0 0 12 22z"
      />
      <path
        fill="#FBBC05"
        d="M6.404 13.9a6.012 6.012 0 0 1 0-3.8v-2.59H3.064a9.996 9.996 0 0 0 0 8.98l3.34-2.59z"
      />
      <path
        fill="#EA4335"
        d="M12 5.977c1.468 0 2.786.505 3.823 1.495l2.868-2.868C16.96 3.046 14.7 2.077 12 2.077a9.996 9.996 0 0 0-8.936 5.523l3.34 2.59C7.19 7.736 9.395 5.977 12 5.977z"
      />
    </svg>
  );
}

export function FacebookF({ size = 18 }: { size?: number }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
      <circle cx="12" cy="12" r="11" fill="#1877F2" />
      <path
        fill="#fff"
        d="M13.4 19.5v-6.7h2.25l.34-2.61H13.4V8.53c0-.76.21-1.27 1.3-1.27h1.39V4.93a18.7 18.7 0 0 0-2.03-.1c-2 0-3.38 1.22-3.38 3.47v1.94H8.42v2.61h2.26v6.65h2.72z"
      />
    </svg>
  );
}

export function InstagramMark({
  size = 18,
  gradientId = "oi-ig-grad",
}: {
  size?: number;
  /** Id univoco per il gradiente, da specificare se la stessa pagina
   *  rende più istanze del logo (es. una nell'header e una in chat),
   *  altrimenti il riferimento al gradient si confonde nel DOM. */
  gradientId?: string;
}) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden>
      <defs>
        <linearGradient id={gradientId} x1="0%" y1="100%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#feda75" />
          <stop offset="35%" stopColor="#fa7e1e" />
          <stop offset="65%" stopColor="#d62976" />
          <stop offset="100%" stopColor="#962fbf" />
        </linearGradient>
      </defs>
      <rect x="2" y="2" width="20" height="20" rx="5" fill={`url(#${gradientId})`} />
      <circle
        cx="12"
        cy="12"
        r="4.2"
        fill="none"
        stroke="#fff"
        strokeWidth="1.8"
      />
      <circle cx="17" cy="7" r="1.2" fill="#fff" />
    </svg>
  );
}

/** Badge "@" per il provider email. */
export function EmailAt({ size = 16 }: { size?: number }) {
  return (
    <span
      aria-hidden
      className="inline-flex items-center justify-center rounded-sm bg-accent text-onAccent"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.7) }}
    >
      @
    </span>
  );
}

/** Dispatcher comodo: passa il provider, ottieni il glifo giusto. */
export function ProviderGlyph({
  provider,
  size = 16,
  gradientId,
}: {
  provider: AuthProvider;
  size?: number;
  gradientId?: string;
}) {
  if (provider === "google") return <GoogleG size={size} />;
  if (provider === "facebook") return <FacebookF size={size} />;
  if (provider === "instagram")
    return <InstagramMark size={size} gradientId={gradientId} />;
  return <EmailAt size={size} />;
}
